import {
  ValidatedFanBuilderLoyaltyCoupon,
  ValidatedFanBuilderLoyaltyCoupon_DiscountType,
} from "gen/api/customer/service_pb"
import { Decimal } from "shared-ts/classes/decimal"

/**
 * CES TerminalId for Fan Builder CustomerService calls from web POS
 * (coupon validate/use, loyalty panel).
 */
export const FAN_BUILDER_WEB_POS_TERMINAL_ID = "1"

/**
 * When Fan Builder coupon “Scan” is armed, this attribute is set on a wrapper so
 * `useBarcodeScanner` can skip pipe-delimited capture (otherwise scans hit product lookup).
 */
export const FAN_BUILDER_COUPON_ARMED_SELECTOR =
  "[data-fan-builder-coupon-armed]"

/** Spread onto the armed wrapper when `listeningForFanBuilderScan` is true. */
export const fanBuilderCouponArmedDomProps = {
  "data-fan-builder-coupon-armed": true,
} as const

/**
 * What a coupon takes off a cart of this subtotal. A percentage is recomputed from its rate rather
 * than read from `discountAmount`, which is only what the coupon was worth against the cart it was
 * validated against — the cart keeps changing until the sale is closed out.
 *
 * Computed in cents with decimal arithmetic to match money.Money.Mult on the server: with binary
 * floats, 29% of $0.50 rounds to $0.14 here and $0.15 there, so the cashier would be shown a penny
 * the sale was not discounted by.
 */
export function fanBuilderCouponDollars(
  coupon: Pick<
    ValidatedFanBuilderLoyaltyCoupon,
    "discountType" | "amount" | "discountAmount"
  >,
  cartSubtotalDollars: number
) {
  if (
    coupon.discountType !==
    ValidatedFanBuilderLoyaltyCoupon_DiscountType.PERCENTAGE
  ) {
    return coupon.discountAmount
  }
  const cents = Decimal.fromBigSource(cartSubtotalDollars)
    .mult(Decimal.fromBigSource(100))
    .mult(Decimal.fromBigSource(coupon.amount))
    .round(0)
  return Number(cents.toFixed()) / 100
}

/**
 * How a coupon takes its discount, for the line under Discount: "7% off" for a percentage coupon,
 * "" for a fixed amount, whose dollars are already the amount on the line.
 *
 * Mirrors couponUseRule in rd/integrations/db/fan_builder_use_coupon_log.go, which produces the
 * same text on the receipt: rounded to two decimals before the trailing zeros come off, because
 * CES sends the rate as a float and 0.07 times 100 is 7.000000000000001.
 */
export function fanBuilderCouponRule(
  coupon: Pick<ValidatedFanBuilderLoyaltyCoupon, "discountType" | "amount">
) {
  if (
    coupon.discountType !==
      ValidatedFanBuilderLoyaltyCoupon_DiscountType.PERCENTAGE ||
    coupon.amount <= 0
  ) {
    return ""
  }
  const pct = (coupon.amount * 100)
    .toFixed(2)
    .replace(/0+$/, "")
    .replace(/\.$/, "")
  return `${pct}% off`
}
